/**
 * AIChatInput - Text input area at the bottom of the AI chat panel
 * Supports auto-growing textarea, Enter to send, Shift+Enter for newline,
 * and a stop button while streaming.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Send, Square } from 'lucide-react';

interface AIChatInputProps {
  onSend: (message: string) => void;
  onCancel: () => void;
  isStreaming: boolean;
  disabled?: boolean;
}

const AIChatInput: React.FC<AIChatInputProps> = ({ onSend, onCancel, isStreaming, disabled = false }) => {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = 22;
    const maxRows = 4;
    const maxHeight = lineHeight * maxRows + 16; // padding
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || isStreaming || disabled) return;
    onSend(trimmed);
    setValue('');
    // Reset height after clearing
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value, isStreaming, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const canSend = value.trim().length > 0 && !isStreaming && !disabled;

  return (
    <div
      style={{
        padding: '12px 16px',
        borderTop: '1px solid var(--tc-border-light, #333333)',
        backgroundColor: 'var(--tc-background-tertiary, #1A1A1A)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '8px',
          backgroundColor: 'var(--tc-background-highlight, #252525)',
          border: '1px solid var(--tc-border-lighter, #444444)',
          borderRadius: '12px',
          padding: '8px 12px',
          transition: 'border-color 0.2s',
        }}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your business data..."
          disabled={disabled}
          rows={1}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--tc-text-primary, #FFFFFF)',
            fontSize: '13px',
            lineHeight: '20px',
            resize: 'none',
            fontFamily: 'inherit',
            padding: 0,
            margin: 0,
            maxHeight: `${22 * 4 + 16}px`,
            overflowY: 'auto',
          }}
        />
        {isStreaming ? (
          <button
            onClick={onCancel}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              color: 'var(--tc-brand-primary, #DA562C)',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)';
            }}
            aria-label="Stop generating"
            title="Stop generating"
          >
            <Square size={14} fill="#EF4444" />
          </button>
        ) : (
          <button
            onClick={handleSend}
            disabled={!canSend}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              backgroundColor: canSend ? 'var(--tc-status-info, #2196F3)' : 'transparent',
              border: canSend ? 'none' : '1px solid var(--tc-border-lighter, #444444)',
              borderRadius: '8px',
              color: canSend ? 'var(--tc-text-primary, #FFFFFF)' : 'var(--tc-text-quaternary, #666666)',
              cursor: canSend ? 'pointer' : 'default',
              flexShrink: 0,
              transition: 'background-color 0.2s, color 0.2s, border-color 0.2s',
            }}
            onMouseEnter={(e) => {
              if (canSend) {
                e.currentTarget.style.backgroundColor = '#2563EB';
              }
            }}
            onMouseLeave={(e) => {
              if (canSend) {
                e.currentTarget.style.backgroundColor = '#3B82F6';
              }
            }}
            aria-label="Send message"
            title="Send message"
          >
            <Send size={16} />
          </button>
        )}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--tc-text-tertiary, #999999)',
          marginTop: '6px',
          textAlign: 'center',
        }}
      >
        AI can make mistakes. Verify important information.
      </div>
    </div>
  );
};

export default AIChatInput;
